import React, { useEffect, useState } from 'react';
import { db } from '../firebase/firebaseConfig';
import { collection, getDocs } from 'firebase/firestore';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  LabelList,
  PieChart,
  Pie,
  Cell,
  Label
} from 'recharts';

const SystemReport = () => {
  const [users, setUsers] = useState([]);
  const [claims, setClaims] = useState([]);
  const [lostItems, setLostItems] = useState([]);
  const [foundItems, setFoundItems] = useState([]);
  const [monthlyActivity, setMonthlyActivity] = useState([]);
  const [selectedMetric, setSelectedMetric] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      const usersSnap = await getDocs(collection(db, 'users'));
      const claimsSnap = await getDocs(collection(db, 'claims'));
      const lostSnap = await getDocs(collection(db, 'lostItems'));

      const usersData = usersSnap.docs;
      const claimsData = claimsSnap.docs;
      const lostData = lostSnap.docs;

      setUsers(usersData);
      setClaims(claimsData);
      setLostItems(lostData);
      setFoundItems(lostData.filter(doc => doc.data().itemStatus === 'Found'));

      const monthly = Array(12).fill(0).map((_, i) => ({
        month: new Date(0, i).toLocaleString('default', { month: 'short' }),
        users: 0,
        lost: 0,
        found: 0,
        claims: 0
      }));

      usersData.forEach(doc => {
        const data = doc.data();
        const dateObj = data.createdAt?.toDate?.() || new Date(data.createdAt);
        const month = dateObj?.getMonth?.();
        if (!isNaN(month)) monthly[month].users += 1;
      });

      lostData.forEach(doc => {
        const data = doc.data();
        const lostDate = data.dateLost?.toDate?.() || new Date(data.dateLost);
        const lostMonth = lostDate?.getMonth?.();
        if (!isNaN(lostMonth)) monthly[lostMonth].lost += 1;

        if (data.itemStatus === 'Found') {
          const rawFound = data.dateFound ?? data.submittedAt ?? data.createdAt;
          const foundDate = rawFound?.toDate?.() || new Date(rawFound);
          const foundMonth = foundDate?.getMonth?.();
          if (!isNaN(foundMonth)) monthly[foundMonth].found += 1;
        }
      });

      claimsData.forEach(doc => {
        const data = doc.data();
        const rawClaim = data.submittedAt ?? data.createdAt;
        const claimDate = rawClaim?.toDate?.() || new Date(rawClaim);
        const claimMonth = claimDate?.getMonth?.();
        if (!isNaN(claimMonth)) monthly[claimMonth].claims += 1;
      });

      setMonthlyActivity(monthly);
    };

    fetchData();
  }, []);

  const stats = [
    {
      name: 'Users',
      value: users.length,
      shape: 'circle',
      color: '#FB8C00', // orange
      icon: 'https://cdn-icons-png.flaticon.com/512/747/747376.png'
    },
    {
      name: 'Claims',
      value: claims.length,
      shape: 'triangle',
      color: '#1976D2',
      icon: 'https://cdn-icons-png.flaticon.com/512/2936/2936937.png'
    },
    {
      name: 'Lost Items',
      value: lostItems.length,
      shape: 'hexagon',
      color: '#D32F2F',
      icon: 'https://cdn-icons-png.flaticon.com/512/865/865969.png'
    },
    {
      name: 'Found Items',
      value: foundItems.length,
      shape: 'rectangle',
      color: '#81C784', // soft green
      icon: 'https://cdn-icons-png.flaticon.com/512/189/189664.png'
    }
  ];

  const getClipPath = (shape) => {
    switch (shape) {
      case 'circle': return 'circle(50% at 50% 50%)';
      case 'triangle': return 'polygon(50% 0%, 0% 100%, 100% 100%)';
      case 'hexagon': return 'polygon(25% 5%, 75% 5%, 100% 50%, 75% 95%, 25% 95%, 0 50%)';
      default: return 'polygon(0 0, 100% 0, 100% 100%, 0 100%)';
    }
  };

  const COLORS = ['#FB8C00', '#1976D2', '#D32F2F', '#81C784'];
  const pieData = stats.map(({ name, value }) => ({ name, value }));

  return (
    <div style={{ padding: '2rem', background: '#f5f7fa', minHeight: '100vh' }}>
      <h2 style={{ textAlign: 'center', color: '#0D47A1', marginBottom: '1rem' }}>System Summary</h2>
      <p style={{ textAlign: 'center', color: '#555', marginBottom: '2rem' }}></p>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2rem', justifyContent: 'center', alignItems: 'center' }}>
        {stats.map((stat, idx) => (
          <div
            key={idx}
            onClick={() => setSelectedMetric(stat.name.toLowerCase())}
            style={{
              width: '180px',
              height: '180px',
              backgroundColor: stat.color,
              clipPath: getClipPath(stat.shape),
              color: '#fff',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '1rem',
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              textAlign: 'center',
              fontWeight: 'bold'
            }}
          >
            <img
              src={stat.icon}
              alt={stat.name}
              style={{ width: '40px', height: '40px', marginBottom: '0.5rem', filter: 'invert(1)' }}
            />
            <h3 style={{ margin: 0, fontSize: '1.8rem' }}>{stat.value}</h3>
            <p style={{ margin: 0, fontSize: '1rem' }}>{stat.name}</p>
          </div>
        ))}
      </div>

      <div style={{ marginTop: '4rem' }}>
        <h3 style={{ textAlign: 'center', color: '#0D47A1' }}>📈 Monthly Trends</h3>
        <ResponsiveContainer width="100%" height={500}>
          <LineChart data={monthlyActivity} margin={{ top: 20, right: 30, left: 20, bottom: 30 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month">
              <Label value="Month" offset={-10} position="insideBottom" />
            </XAxis>
            <YAxis allowDecimals={false}>
              <Label value="Count" angle={-90} position="insideLeft" style={{ textAnchor: 'middle' }} />
            </YAxis>
            <Tooltip />
            <Legend verticalAlign="top" height={36} />

            {(selectedMetric === 'all' || selectedMetric === 'users') &&
              <Line type="monotone" dataKey="users" stroke="#FB8C00" strokeWidth={2} name="Users">
                <LabelList dataKey="users" position="top" fill="#FB8C00" fontSize={12} />
              </Line>
            }
            {(selectedMetric === 'all' || selectedMetric === 'lost items') &&
              <Line type="monotone" dataKey="lost" stroke="#D32F2F" strokeWidth={2} name="Lost Items">
                <LabelList dataKey="lost" position="top" fill="#D32F2F" fontSize={12} />
              </Line>
            }
            {(selectedMetric === 'all' || selectedMetric === 'found items') &&
              <Line type="monotone" dataKey="found" stroke="#81C784" strokeWidth={2} name="Found Items">
                <LabelList dataKey="found" position="top" fill="#81C784" fontSize={12} />
              </Line>
            }
            {(selectedMetric === 'all' || selectedMetric === 'claims') &&
              <Line type="monotone" dataKey="claims" stroke="#1976D2" strokeWidth={2} name="Claims">
                <LabelList dataKey="claims" position="top" fill="#1976D2" fontSize={12} />
              </Line>
            }
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div style={{ marginTop: '4rem' }}>
        <h3 style={{ textAlign: 'center', color: '#0D47A1' }}>📊 Summary Breakdown</h3>
        <ResponsiveContainer width="100%" height={400}>
          <PieChart>
            <Pie
              data={pieData}
              cx="50%"
              cy="50%"
              outerRadius={150}
              fill="#8884d8"
              dataKey="value"
              label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
            >
              {pieData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default SystemReport;
